import { compareAsc, parseISO } from "date-fns";
import { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";

import { faCirclePlus, faEye } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";
import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import LoadingAnimation from "components/organisms/LoadingAnimation";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import useAccess from "hooks/useAccess";
import useQueryParamsState from "hooks/useQueryParamsState";
import { useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { EmptyBox, ErrorBox } from "pages/Sikring/components/InfoBox";
import { SikringsordreDrawer } from "pages/Sikring/oversikt/SikringsordreDrawer";
import { SikringsordreTable, SortDirection, SortKey } from "pages/Sikring/oversikt/SikringsordreTable";
import { STATUS_LABEL, isDeleted, priceOf, totalVolumeKwh } from "pages/Sikring/sikringUtils";
import { Sikringsordre, Sikringsordrestatus } from "pages/Sikring/types";

/** Standardfilteret (alle unntatt slettede og delvis slettede). Står også i URL-en (?status=), så verdien må være stabil. */
const ALL = "ALLE";
type StatusFilter = typeof ALL | Sikringsordrestatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
	{ value: ALL, label: "Alle" },
	{ value: Sikringsordrestatus.AwaitingSignature, label: STATUS_LABEL[Sikringsordrestatus.AwaitingSignature] },
	{ value: Sikringsordrestatus.AwaitingEarlierSikring, label: STATUS_LABEL[Sikringsordrestatus.AwaitingEarlierSikring] },
	{ value: Sikringsordrestatus.AwaitingManualContract, label: STATUS_LABEL[Sikringsordrestatus.AwaitingManualContract] },
	{ value: Sikringsordrestatus.AwaitingContract, label: STATUS_LABEL[Sikringsordrestatus.AwaitingContract] },
	{ value: Sikringsordrestatus.AwaitingSecureVolume, label: STATUS_LABEL[Sikringsordrestatus.AwaitingSecureVolume] },
	{ value: Sikringsordrestatus.Completed, label: STATUS_LABEL[Sikringsordrestatus.Completed] },
	{ value: Sikringsordrestatus.Failed, label: STATUS_LABEL[Sikringsordrestatus.Failed] },
	{ value: Sikringsordrestatus.ContractFailed, label: STATUS_LABEL[Sikringsordrestatus.ContractFailed] },
	{ value: Sikringsordrestatus.Deleted, label: STATUS_LABEL[Sikringsordrestatus.Deleted] },
	{ value: Sikringsordrestatus.PartiallyDeleted, label: STATUS_LABEL[Sikringsordrestatus.PartiallyDeleted] },
];

/**
 * Statuser som endres av backend uten at brukeren gjør noe: UtilityCloud jobber med ordren, eller den venter på kontrakten
 * til en tidligere sikring. Listen hentes på nytt jevnlig så lenge en ordre står i en av dem.
 */
const AWAITING_UTILITYCLOUD: Sikringsordrestatus[] = [
	Sikringsordrestatus.AwaitingContract,
	Sikringsordrestatus.AwaitingSecureVolume,
	Sikringsordrestatus.AwaitingEarlierSikring,
];
const POLL_INTERVAL_MS = 10_000;

const SEARCH_FIELDS: { label: string; value: (order: Sikringsordre) => string | null | undefined }[] = [
	{ label: "kunde", value: (o) => o.kunde?.navn },
	{ label: "org.nr.", value: (o) => o.kunde?.organisasjonsnummer },
	{ label: "kontaktperson", value: (o) => o.kunde?.kontaktperson },
	{ label: "målepunkt-ID", value: (o) => o.maalepunkter?.join(" ") },
	{ label: "ordre-ID", value: (o) => o.ordreId },
	{ label: "produkt-ID", value: (o) => o.produktId },
	{ label: "sikringsordre-ID", value: (o) => o.id },
	{ label: "prisområde", value: (o) => o.sikring.prisomraade },
];

const SEARCH_FIELDS_TEXT = `Søk i ${SEARCH_FIELDS.map((f) => f.label).join(", ")}`;

/** Sammenligner stigende. Tomme verdier havner sist i stigende rekkefølge og først i synkende. */
const COMPARATORS: Record<SortKey, (a: Sikringsordre, b: Sikringsordre) => number> = {
	[SortKey.Status]: (a, b) => STATUS_LABEL[a.status].localeCompare(STATUS_LABEL[b.status], "nb"),
	[SortKey.Customer]: (a, b) => compareNullable(a.kunde?.navn || null, b.kunde?.navn || null, (x, y) => x.localeCompare(y, "nb")),
	[SortKey.Created]: (a, b) => compareAsc(parseISO(a.opprettet), parseISO(b.opprettet)),
	[SortKey.Period]: (a, b) => compareAsc(parseISO(a.sikring.start), parseISO(b.sikring.start)),
	[SortKey.Volume]: (a, b) => compareNullable(totalVolumeKwh(a), totalVolumeKwh(b), (x, y) => x - y),
	[SortKey.Price]: (a, b) => compareNullable(priceOf(a.sikring), priceOf(b.sikring), (x, y) => x - y),
};

function compareNullable<T>(a: T | null, b: T | null, compare: (a: T, b: T) => number): number {
	if (a === null && b === null) return 0;
	if (a === null) return 1;
	if (b === null) return -1;
	return compare(a, b);
}

const DEFAULT_SORT_KEY = SortKey.Created;

function flipDirection(direction: SortDirection): SortDirection {
	return direction === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending;
}

function defaultDirection(key: SortKey): SortDirection {
	return key === SortKey.Status || key === SortKey.Customer ? SortDirection.Ascending : SortDirection.Descending;
}

/** Standardfilteret viser alt unntatt slettede ordrer - de vises bare med eget filter. */
function matchesStatusFilter(order: Sikringsordre, filter: StatusFilter) {
	return filter === ALL ? !isDeleted(order.status) : order.status === filter;
}

export default function SikringsordreListPage() {
	const navigate = useNavigate();
	const access = useAccess();
	const canCreate = access.includes("manage-sikring");

	const [status, setStatus] = useQueryParamsState("status");
	const [search, setSearch] = useQueryParamsState("sok");
	const [sortParam] = useQueryParamsState("sorter");
	const [directionParam] = useQueryParamsState("retning");
	// I URL-en, så en ordre kan lenkes til og tilbake-knappen lukker detaljvisningen.
	const [viewedOrderId, setViewedOrderId] = useQueryParamsState("ordre");
	const location = useLocation();
	const activeStatusFilter = (status as StatusFilter) || ALL;
	const searchText = search ?? "";
	const sortKey = Object.values(SortKey).find((key) => key === sortParam) ?? DEFAULT_SORT_KEY;
	const sortDirection = Object.values(SortDirection).find((d) => d === directionParam) ?? defaultDirection(sortKey);

	/** Undefined fjerner parameteren. */
	function hrefWith(changes: Record<string, string | undefined>): string {
		const params = new URLSearchParams(location.search);
		Object.entries(changes).forEach(([key, value]) => (value === undefined ? params.delete(key) : params.set(key, value)));
		return `?${params.toString()}`;
	}

	function sortHref(key: SortKey): string {
		return hrefWith({ sorter: key, retning: key === sortKey ? flipDirection(sortDirection) : defaultDirection(key) });
	}

	const {
		data: orders,
		isLoading,
		isError,
		refetch,
	} = useStrombestillingApiQuery<Sikringsordre[]>("/sikringsordre", ["sikringsordre"], true, (query) =>
		query.state.data?.some((o) => AWAITING_UTILITYCLOUD.includes(o.status)) ? POLL_INTERVAL_MS : false
	);

	const all = useMemo(() => orders ?? [], [orders]);

	const countPerStatus = useMemo(() => {
		const res: Record<string, number> = { [ALL]: all.filter((o) => matchesStatusFilter(o, ALL)).length };
		all.forEach((o) => {
			res[o.status] = (res[o.status] ?? 0) + 1;
		});
		return res;
	}, [all]);

	const rows = useMemo(() => {
		const searchString = searchText.trim().toLowerCase();
		return all
			.filter((o) => matchesStatusFilter(o, activeStatusFilter))
			.filter((o) => {
				if (!searchString) return true;
				return SEARCH_FIELDS.some((field) => (field.value(o) ?? "").toLowerCase().includes(searchString));
			})
			.sort((a, b) => {
				const order = COMPARATORS[sortKey](a, b);
				// Like verdier sorteres med nyeste først, så rekkefølgen er stabil.
				return (sortDirection === SortDirection.Ascending ? order : -order) || COMPARATORS[SortKey.Created](b, a);
			});
	}, [all, activeStatusFilter, searchText, sortKey, sortDirection]);

	const viewedOrder = viewedOrderId ? all.find((o) => o.id === viewedOrderId) : undefined;

	const emptyMessage = all.length === 0 ? "Det finnes ingen sikringsordrer ennå." : "Ingen sikringsordrer samsvarer med filteret.";

	return (
		<Flex style={{ maxWidth: "1240px" }} gap="1rem" direction={Direction.column}>
			<Flex direction={Direction.row} justifyContent={Justify.spaceBetween} alignItems={Justify.flexStart} gap="24px">
				<div>
					<MainLayoutPageHeader title="Sikringsordrer" />
				</div>
				{canCreate ? (
					<HeaderButton
						type="button"
						icon={<FontAwesomeIcon icon={faCirclePlus} />}
						label="Opprett sikring"
						onClick={() => navigate("/sikring/ny")}
					/>
				) : (
					<ViewerHint>
						<FontAwesomeIcon icon={faEye} style={{ marginRight: "6px" }} />
						Du har kun lesetilgang til sikringer. Opprettelse er skjult.
					</ViewerHint>
				)}
			</Flex>

			{isLoading && <LoadingAnimation />}

			{isError && (
				<ErrorBox
					heading="Kunne ikke hente sikringsordrer"
					body="Listen over sikringsordrer kunne ikke lastes. Du kan fortsatt opprette en ny sikring."
					onRetry={() => refetch()}
				/>
			)}

			{!isLoading && !isError && (
				<>
					<FilterRow>
						{STATUS_FILTERS.map((f) => (
							<Chip
								key={f.value}
								type="button"
								$active={activeStatusFilter === f.value}
								onClick={() => setStatus(f.value === ALL ? undefined : f.value)}
							>
								{f.label} ({countPerStatus[f.value] ?? 0})
							</Chip>
						))}
						<Divider />
						<SearchInput
							type="search"
							placeholder="Søk i kunde, org.nr. og ID-er …"
							title={SEARCH_FIELDS_TEXT}
							aria-label={SEARCH_FIELDS_TEXT}
							value={searchText}
							onChange={(e) => setSearch(e.target.value || undefined)}
						/>
					</FilterRow>

					<h2 style={{ margin: 0 }}>Sikringsordrer ({rows.length})</h2>
					{viewedOrderId && !viewedOrder && (
						<NotFoundText>
							Fant ikke sikringsordren i lenken. Den kan være slettet eller lenken feil. <Link to={hrefWith({ ordre: undefined })}>Lukk</Link>
						</NotFoundText>
					)}
					{rows.length === 0 && (
						<EmptyBox
							title={emptyMessage}
							hint={canCreate && all.length === 0 ? "Bruk «Opprett sikring» for å opprette en ordre med prissikring." : undefined}
						/>
					)}
					{rows.length > 0 && (
						<SikringsordreTable
							rows={rows}
							sortKey={sortKey}
							sortDirection={sortDirection}
							sortHref={sortHref}
							orderHref={(order) => hrefWith({ ordre: order.id })}
						/>
					)}
				</>
			)}

			{viewedOrder && <SikringsordreDrawer order={viewedOrder} onClose={() => setViewedOrderId(undefined)} />}
		</Flex>
	);
}

const NotFoundText = styled.p`
	margin: 0;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const HeaderButton = styled(Button)`
	flex: 0 0 auto;
	white-space: nowrap;
`;

const ViewerHint = styled.div`
	font-size: 0.8125rem;
	line-height: 1.5;
	color: ${colors.grey700};
	max-width: 240px;
	text-align: right;
`;

const FilterRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
`;

const Chip = styled.button<{ $active: boolean }>`
	height: 32px;
	padding: 0 16px;
	border-radius: 6px;
	border: none;
	cursor: pointer;
	font-weight: 500;
	font-size: 0.875rem;
	${(props) =>
		props.$active
			? css`
					background: ${colors.blue};
					color: ${colors.white};
				`
			: css`
					background: ${colors.blue200};
					color: ${colors.blue800};
				`}
`;

const Divider = styled.span`
	width: 1px;
	height: 26px;
	background: ${colors.grey300};
	margin: 0 6px;
`;

const SearchInput = styled.input`
	height: 32px;
	min-width: 280px;
	border: none;
	border-radius: 6px;
	box-shadow: inset 0 0 0 1px ${colors.grey400};
	padding: 0 10px;
	font-size: 0.8125rem;
	color: ${colors.grey900};
	outline: none;
`;
